/** @format */

import { useCallback, useEffect, useState } from "react";
import { Brand, Question, QuestionNode, flattenTree } from "@question-bank/shared";
import { getQuestionChildren } from "../api/questions";

interface UseParentGroupReturn {
	/** The ribbon root question, used as the editable "parent". */
	parent: Question | null;
	/** Every item in the group, including the root, sorted by question number. */
	items: Question[];
	/** Child parts only (group items excluding the root). */
	children: Question[];
	/** The raw tree as delivered by the API (nested QuestionNode[]). */
	tree: QuestionNode[];
	isLoading: boolean;
	error: string | null;
	reload: () => void;
}

/**
 * Loads a whole ribbon group by its `parentQuestionId`. The group's root
 * (`isRoot === true`, falling back to the first item) is surfaced as the
 * `parent`; the remaining items are exposed as `children`.
 *
 * The original nested tree from the API is also exposed via `tree` for
 * consumers that need hierarchical rendering.
 */
export const useParentGroup = (brand: Brand, parentQuestionId: string): UseParentGroupReturn => {
	const [items, setItems] = useState<Question[]>([]);
	const [tree, setTree] = useState<QuestionNode[]>([]);
	const [parent, setParent] = useState<Question | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [reloadToken, setReloadToken] = useState(0);

	const reload = useCallback(() => setReloadToken((token) => token + 1), []);

	useEffect(() => {
		let isCancelled = false;
		setIsLoading(true);
		setError(null);
		setParent(null);
		setItems([]);
		setTree([]);

		getQuestionChildren(brand, parentQuestionId)
			.then((response) => {
				if (isCancelled) {
					return;
				}
				setTree(response.items);
				const groupItems = flattenTree(response.items);
				setItems(groupItems);
				setParent(groupItems.find((item) => item.isRoot) ?? groupItems[0] ?? null);
			})
			.catch((err) => {
				if (!isCancelled) {
					setError(err instanceof Error ? err.message : "failed to load question");
				}
			})
			.finally(() => {
				if (!isCancelled) {
					setIsLoading(false);
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [brand, parentQuestionId, reloadToken]);

	const children = parent ? items.filter((item) => item.id !== parent.id) : items;

	return { parent, items, children, tree, isLoading, error, reload };
};
